// src/components/admin/teamTaskbox/TaskCard.js
import React from 'react';
import {
    Clock, Calendar, Users, MessageSquare, Paperclip,
    Flag, Copy, Trash2, AlertTriangle
} from 'lucide-react';
import teamTasksService from '../../../services/teamTasks.service';

const TaskCard = ({ task, viewMode, onClick, onClone, onDelete }) => {
    const statusColors = {
        pending: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
        in_progress: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
        under_review: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
        completed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
        on_hold: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
    };

    const priorityColors = {
        immediate: 'text-red-600 dark:text-red-400',
        urgent: 'text-orange-600 dark:text-orange-400',
        next_in_line: 'text-yellow-600 dark:text-yellow-400',
        scheduled: 'text-green-600 dark:text-green-400',
        deferred: 'text-gray-600 dark:text-gray-400'
    };

    const progress = teamTasksService.calculateProgress(task);
    const { subtasksTotal, subtasksCompleted } = teamTasksService.getSubtaskCounts(task);
    const isOverdue = task.overdue_days > 0;

    // Extract assigned employee IDs - handle various data formats from backend
    const getAssignedEmployeeIds = (task) => {
        // Direct array (UUIDs are strings, keep them as strings)
        if (Array.isArray(task.assigned_employee_ids)) {
            return task.assigned_employee_ids.map(id => String(id).trim());
        }

        // Comma-separated string
        if (typeof task.assigned_employee_ids === 'string' && task.assigned_employee_ids.trim()) {
            return task.assigned_employee_ids.split(',').map(id => String(id).trim());
        }

        // Single value (UUID or ID)
        if (task.assigned_employee_ids) {
            return [String(task.assigned_employee_ids).trim()];
        }

        return [];
    };

    const assignedEmployeeIds = getAssignedEmployeeIds(task);
    const assignedEmployeesCount = assignedEmployeeIds.length;

    // Handle estimated hours - show actual value or null
    const estimatedHours = task.estimated_hours ? parseFloat(task.estimated_hours) : null;

    const formatDate = (dateString) => {
        if (!dateString) return null;
        // Extract YYYY-MM-DD from ISO string
        const dateOnly = dateString.split('T')[0];
        const [year, month, day] = dateOnly.split('-');
        // Create date in local timezone without UTC conversion
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const handleClone = (e) => {
        e.stopPropagation();
        onClone();
    };

    const handleDelete = (e) => {
        e.stopPropagation();
        onDelete();
    };

    if (viewMode === 'list') {
        return (
            <div
                onClick={onClick}
                className="bg-white dark:bg-gray-800 rounded-lg shadow hover:shadow-lg transition-all cursor-pointer p-4"
            >
                <div className="flex items-center gap-4">
                    {/* Priority Flag */}
                    <Flag className={`w-5 h-5 ${priorityColors[task.priority]}`} fill="currentColor" />

                    {/* Task Info */}
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                                {task.title}
                            </h3>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[task.status]}`}>
                                {task.status.replace('_', ' ')}
                            </span>
                            {isOverdue && (
                                <span className="flex items-center gap-1 px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded text-xs font-medium">
                                    <AlertTriangle className="w-3 h-3" />
                                    {task.overdue_days} days overdue
                                </span>
                            )}
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-1">
                            {task.description || 'No description'}
                        </p>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                        {task.deadline && (
                            <div className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {formatDate(task.deadline)}
                            </div>
                        )}
                        <div className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {assignedEmployeesCount}
                        </div>
                        {task.comments_count > 0 && (
                            <div className="flex items-center gap-1">
                                <MessageSquare className="w-4 h-4" />
                                {task.comments_count}
                            </div>
                        )}
                        {subtasksTotal > 0 && (
                            <div className="flex items-center gap-1">
                                <span>{subtasksCompleted}/{subtasksTotal}</span>
                            </div>
                        )}
                    </div>

                    {/* Progress */}
                    <div className="w-32">
                        <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
                            <span>Progress</span>
                            <span>{progress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div
                                className="bg-blue-600 h-2 rounded-full transition-all"
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleClone}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                            title="Clone task"
                        >
                            <Copy className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                        </button>
                        <button
                            onClick={handleDelete}
                            className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                            title="Delete task"
                        >
                            <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Grid view
    return (
        <div
            onClick={onClick}
            className="bg-white dark:bg-gray-800 rounded-lg shadow hover:shadow-xl transition-all cursor-pointer overflow-hidden"
        >
            {/* Header */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-start justify-between mb-2">
                    <Flag className={`w-5 h-5 ${priorityColors[task.priority]} flex-shrink-0`} fill="currentColor" />
                    <div className="flex gap-1">
                        <button
                            onClick={handleClone}
                            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                            title="Clone task"
                        >
                            <Copy className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                        </button>
                        <button
                            onClick={handleDelete}
                            className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors"
                            title="Delete task"
                        >
                            <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                        </button>
                    </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1 line-clamp-2">
                    {task.title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 min-h-[2.5rem]">
                    {task.description || 'No description'}
                </p>
            </div>

            {/* Body */}
            <div className="p-4 space-y-3">
                {/* Status */}
                <div className="flex items-center gap-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[task.status]}`}>
                        {task.status.replace('_', ' ').toUpperCase()}
                    </span>
                    {isOverdue && (
                        <span className="flex items-center gap-1 px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-full text-xs font-medium">
                            <AlertTriangle className="w-3 h-3" />
                            {task.overdue_days}d overdue
                        </span>
                    )}
                </div>

                {/* Progress Bar */}
                <div>
                    <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-2">
                        <span>Progress</span>
                        <span className="font-semibold">{progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                            className={`h-2 rounded-full transition-all ${
                                progress === 100 ? 'bg-green-600' : 'bg-blue-600'
                            }`}
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                </div>

                {/* Meta Info */}
                <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                    {task.deadline && (
                        <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            <span>Due: {formatDate(task.deadline)}</span>
                        </div>
                    )}
                    <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span>{estimatedHours ? `${estimatedHours}h estimated` : 'Not set'}</span>
                    </div>
                </div>

                {/* Footer Stats */}
                <div className="flex items-center gap-4 pt-3 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-400">
                    <div className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        <span>{assignedEmployeesCount}</span>
                    </div>
                    {subtasksTotal > 0 && (
                        <div className="flex items-center gap-1">
                            <span>{subtasksCompleted}/{subtasksTotal}</span>
                        </div>
                    )}
                    {task.comments_count > 0 && (
                        <div className="flex items-center gap-1">
                            <MessageSquare className="w-4 h-4" />
                            <span>{task.comments_count}</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TaskCard;
